/**
 * 문서 유형 선택 모달
 * 점검을 기반으로 생성할 문서 유형(Composer 양식)을 선택합니다.
 *
 * **카드를 손으로 나열하지 말 것** — 카드는 서버가 category·entitlement로 필터해 내려준
 * enabled 템플릿뿐이고, 카드 전용 가용성·중복 조회도 두지 않는다.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, Button, Modal } from '@safetyhub/ui';
import { apiErrorMessage } from '../../lib/api';
import { getReports, generateImprovementCandidates, type Report } from '../../lib/reports';
import { InspectionSelectModal } from './InspectionSelectModal';
import { ChevronRightIcon } from '../icons';
import { useAuthStore } from '../../stores/auth';
import type { SiteContact } from '../../lib/tbm';
import { ReportDelegationModal, DelegationApprovalLineValue } from './ReportDelegationModal';
import { createDelegatedComposedReport } from '../../lib/reports';
import { resolvePostSubmitEntryRoute } from '../../pages/reports/postSubmitFlows';
import { useToastStore } from '../../stores/toast';
import { useEnabledComposerTemplates } from '../../hooks/useEnabledComposerTemplates';
import { isSplitCapable, useSplitGroupEntry } from './useSplitGroupEntry';
import { allowsMultipleInspections, withSubjectParticle, type ReportTemplate } from '@safetyhub/shared';
import {
  findEntryUserInputMeta,
  collectImprovementParts,
  composedEntryGuidanceKey,
  type EntryUserInputMeta,
} from './composer/improvementCandidates';
import { AlertTriangleIcon as ExclamationIcon, XIcon } from '../icons';
import { MODAL_LAYER } from '../modalLayers';

/**
 * 보고서 목록에서 Composer 양식 보고서가 사용한 템플릿 키를 수집한다.
 * 서버 목록 API는 CANCELLED를 기본 제외하므로 남은 건 모두 살아 있는 보고서다.
 * 모든 행이 Composer 문서라 `templateKey`는 항상 있다(`template_key` NOT NULL).
 */
function collectComposedTemplateKeys(reports: Report[]): string[] {
  return [...new Set(reports.map((r) => r.templateKey))];
}

/**
 * 이 템플릿은 한 점검으로 **여러 건**을 만들 수 있는가 — 결정 2026-09-01 §③-4.
 *
 * "여러 건 허용"은 분할만의 성질이 아니다. `duplicatePolicy:'allow'` 템플릿도 서버는 N건을
 * 허용하는데 카드만 잠그면 같은 모순이 된다. 그래서 술어를 한 곳에 모아 **중복 잠금 축 전부**
 * (카드 잠금 → 그 잠금을 읽는 진입 차단 → 진입 직전 재조회 차단)에 같은 것을 쓴다.
 *
 * ⚠️ 현재 `allow` 템플릿은 **dev 0건**이라 동작 차이가 관측되지 않는다 — 이 확장이 지켜지는
 * 자리는 실사용이 아니라 spec뿐이다(결정 §③-4가 그 사실을 명시한다). 지금 넓히는 이유는 술어가
 * 여기 한 곳에 모여 있기 때문이고, 나중에 하면 세 자리를 다시 찾아야 한다.
 */
function allowsMultiple(tpl: ReportTemplate): boolean {
  return isSplitCapable(tpl) || tpl.policy?.duplicatePolicy === 'allow';
}

interface ReportTypeSelectModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 선택된 점검 ID */
  inspectionId: string;
  /** 조직 ID */
  companyId: string;
  /** 현장 ID */
  siteId: string;
  /** 모달 닫기 */
  onClose: () => void;
  // (조직 category·인증 배지·중복 판정 prop을 받지 않는다 — 템플릿 목록은 서버가
  //  category·entitlement로 필터해 내려준다.)
}

export function ReportTypeSelectModal({
  isOpen,
  inspectionId,
  companyId,
  siteId,
  onClose,
}: ReportTypeSelectModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // 위임 생성 직후 목적지 판정(`resolvePostSubmitEntryRoute`)의 입력 — 형제 대기 갈래가 이 축을 본다.
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Composer enabled 템플릿(동적 COMPOSED 카드) — 공용 훅이 조회·실패 폴백을 소유한다(훅 JSDoc 참조).
  // 모달은 닫혀 있는 동안 요청을 만들지 않으므로 `isOpen`을 enabled 축으로 넘긴다.
  // (로딩 표시는 쓰지 않는다 — 종전처럼 목록이 도착하면 카드가 붙는다.)
  const { templates: composerTemplates } = useEnabledComposerTemplates(companyId, { enabled: isOpen });

  // 점검 앵커 COMPOSED 중복 생성 가드 — 이 점검에 이미 생성된 Composer 템플릿 키 집합.
  // (구 SMS 카드의 '이미 생성됨' 배지 + 카드 비활성 가드를 템플릿 단위로 승계)
  // 카드가 실제로 그려질 때(템플릿 로드 후)만 조회해 불필요한 요청을 만들지 않는다.
  const [existingComposedKeys, setExistingComposedKeys] = useState<string[]>([]);
  useEffect(() => {
    if (!isOpen || !inspectionId || !companyId || composerTemplates.length === 0) {
      setExistingComposedKeys([]);
      return;
    }
    let cancelled = false;
    getReports(companyId, { inspectionId })
      .then((result) => {
        if (!cancelled) setExistingComposedKeys(collectComposedTemplateKeys(result.data));
      })
      .catch(() => { /* 조회 실패 시 중복 없음으로 처리 — 진입 직전 재조회 + 서버가 최종 차단 */ });
    return () => { cancelled = true; };
  }, [isOpen, inspectionId, composerTemplates.length, companyId]);

  /**
   * 카드를 "이미 생성됨"으로 잠글 것인가.
   *
   * **여러 건 허용 템플릿은 제외**한다(§③-4). 분할은 중복 축이 템플릿이 아니라 **행**이라(서버
   * `findSplitSiblingOwnership`, 2026-09-10 행 축 전환) 형제 1건이 생긴 순간 카드를 잠그면
   * 나머지를 영영 못 만들고, `duplicatePolicy:'allow'`는 애초에 서버가 N건을 허용한다. 분할
   * 템플릿의 "이미 있음"은 그룹 선택 모달이 **행별로** 보여 준다.
   */
  const isComposedAlreadyCreated = (tpl: ReportTemplate) =>
    !allowsMultiple(tpl) && existingComposedKeys.includes(tpl.key);

  // 로딩 상태
  const [preparingStep, setPreparingStep] = useState<'loading' | 'ai'>('loading');
  const [isPreparingComposed, setIsPreparingComposed] = useState(false);
  // Composer 템플릿의 점검 선택 단계(`policy.allowMultipleInspections` — 판정은 shared
  // `allowsMultipleInspections`) — 어느 템플릿을 위해 점검 선택 모달이 열렸는지(2026-09-14 다중
  // 점검 합본). null = 닫힘.
  const [composedInspectionSelect, setComposedInspectionSelect] = useState<{
    tpl: ReportTemplate;
    entryGuidance?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryFn, setRetryFn] = useState<(() => void) | null>(null);

  const { showToast } = useToastStore();

  // COMPOSED(Composer 템플릿) 위임 모달 — delegationMeta.enabled 템플릿 카드에서 오픈
  const [composedDelegationTemplate, setComposedDelegationTemplate] = useState<ReportTemplate | null>(null);

  // 분할 생성(1점검 → N문서) 그룹 선택 — 결정 2026-09-01 §③. 절차는 공용 훅이 소유하고
  // (위저드 진입로와 같은 것을 쓴다 — F-1), 로딩·에러 표시와 이동 경로만 여기 남는다.
  const splitEntry = useSplitGroupEntry({
    onCombined: (tpl, entryGuidance) => { void prepareAndEnterComposed(tpl, entryGuidance); },
    onCreated: (tpl, firstReportId) => {
      onClose();
      navigate(
        `/reports/new/composed/${encodeURIComponent(tpl.key)}?editReportId=${firstReportId}`,
        { state: { from: location.pathname, fromLabel: '점검 상세' } },
      );
    },
    onNoneCreated: () => {
      // 전건 skip — 새로 연 초안이 없으니 열 곳도 없다. 종전대로 목록(안내 토스트와 짝).
      onClose();
      navigate('/reports');
    },
    // 배너는 남겨 둔다 — 사용자가 분할 모달을 닫으면 토스트가 사라진 뒤에도 사유가 보인다.
    onCreateError: (message) => setError(message),
    // 층은 여는 쪽이 정한다 — 이 모달은 문서 유형 선택(z-[70]) 위에 뜬다.
    layer: MODAL_LAYER.sub,
  });

  // Composed 템플릿 진입 전 사용자 입력 패널 상태(템플릿 전역 entryUserInput 켜짐 시)
  const [composedEntryTemplateKey, setComposedEntryTemplateKey] = useState<string | null>(null);
  const [composedEntryValue, setComposedEntryValue] = useState('');
  const composedEntryPanelRef = useRef<HTMLDivElement>(null);

  // 모달 오픈 시 body 스크롤 잠금 — base `Modal`은 `overflow:hidden`만 건다. 이관 전부터 있던
  // `position:fixed` + 스크롤 위치 복원은 base에 없는 축이라 그대로 둔다(둘 다 닫힘 시 빈 값으로 복원).
  const scrollYRef = useRef(0);
  useEffect(() => {
    if (!isOpen) return;
    scrollYRef.current = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setComposedEntryTemplateKey(null);
    setComposedEntryValue('');
    onClose();
  };

  /**
   * COMPOSED 템플릿 카드 선택: AI 후보(sortableSelect 소스)를 사전 생성 후 작성 페이지 진입.
   * 사전 생성으로 서버 캐시에 적재되어 작성 페이지의 재요청이 캐시 히트로 즉시 반환된다.
   * 실패해도 진입은 진행 — 작성 페이지가 후보 없음/재시도를 처리하므로 진입을 차단하지 않는다
   * (legacy SMS의 개선 의견 사전 생성 실패 처리와 동일한 톤 — 조용히 무시).
   */
  const handleSelectComposedTemplate = async (tpl: ReportTemplate, entryGuidance?: string) => {
    if (isPreparingComposed) return;
    if (isComposedAlreadyCreated(tpl)) return;

    // 진입 직전 서버에서 최신 목록 재조회하여 중복 방지 (구 SMS 카드 동형 — 모달을 열어둔 사이
    // 다른 탭/사용자가 만든 보고서를 잡는다). 조회 실패 시 낙관적 진행 — 서버가 최종 차단.
    // ⚠️ **여러 건 허용 템플릿은 여기서 걸러선 안 된다**(§③-4) — 분할은 중복 축이 템플릿이 아니라
    // **행**이라(2026-09-10 행 축 전환, 그 전에는 그룹) 형제 1건이 생긴 순간 나머지까지 못 만들게
    // 되고(아래 계획 조회의 `existing`이 그 자리), `allow`는 서버가 N건을 허용하는데 여기서만
    // 막으면 화면과 서버가 갈린다.
    if (companyId && inspectionId && !allowsMultiple(tpl)) {
      try {
        const latest = await getReports(companyId, { inspectionId });
        const latestKeys = collectComposedTemplateKeys(latest.data);
        if (latestKeys.includes(tpl.key)) {
          setExistingComposedKeys(latestKeys);
          setComposedEntryTemplateKey(null);
          setError(`이 점검으로 이미 ${withSubjectParticle(tpl.name)} 생성되어 있습니다.`);
          return;
        }
      } catch {
        // 조회 실패 시 낙관적으로 진행 (서버에서 409 처리)
      }
    }

    // 점검 선택 단계 — 포함할 점검을 고르는 모달(현재 점검 기본 선택, 최소 1개, 정렬)을 먼저
    // 띄우고, 확정에서 prepareAndEnterComposed(ids)로 잇는다 (2026-09-14 사용자 결정 —
    // 「N개 점검의 내용이 모두 한 결과서에」, 분할 아님). 템플릿 키 하드 분기가 아니라 정책
    // 선언으로 판정한다(provider registry + policy 선언 동형, 결정 2026-09-01).
    // 분할(split)과는 다른 축이고 두 선언을 함께 둔 양식은 없다 — 함께 두면 이 단계가 먼저다.
    //
    // ⚠️ **판정은 shared `allowsMultipleInspections`가 소유한다 — 여기 식을 손으로 쓰지 말 것.**
    // 2026-10-09에 구 `policy.inspectionSelection === 'multi'` 리터럴 비교에서 옮겼다(결정
    // `2026-10-09-the-enforcement-expression-is-the-canonical-gate.md` 축 ②: 그 enum을 직교 불리언
    // 둘 `allowNoInspection`·`allowMultipleInspections`로 **대체**했다). 그 전에는 이 선언을 읽는
    // 곳이 **이 자리와 admin 경고뿐이고 서버는 아무것도 집행하지 않았다.**
    //
    // ⚠️ **`judgeInspectionCount`를 여기 쓰지 말 것 — 축이 다르다.** 그 함수는 「고른 개수가
    // 허용되나」(서버 집행용, 입력이 개수)이고, 이 자리가 묻는 것은 **「복수를 고르게 할 것인가」**
    // 다(아직 아무것도 안 골랐다). 둘을 섞으면 단수 양식에서 「1건이니 허용」으로 통과해 **모달이
    // 안 뜨는 것이 아니라 뜨게** 된다.
    if (allowsMultipleInspections(tpl.policy) && companyId && inspectionId && siteId) {
      setError(null);
      setRetryFn(null);
      setComposedInspectionSelect({ tpl, entryGuidance });
      return;
    }

    // 분할 계획 조회 — 정책에 split 선언이 있는 템플릿만. 선언이 없으면 서버도 반드시
    // `plan: null`을 내므로(buildPlan이 정책 부재에서 null) 왕복을 아끼는 **보수적** 게이트다.
    if (isSplitCapable(tpl) && companyId && inspectionId && siteId) {
      setIsPreparingComposed(true);
      setPreparingStep('loading');
      setError(null);
      setRetryFn(null);
      try {
        const outcome = await splitEntry.begin(
          tpl,
          { companyId, siteId, inspectionIds: [inspectionId] },
          entryGuidance,
        );
        setIsPreparingComposed(false);
        // `'not-split'` = 이 컨텍스트는 분할하지 않는다 → 그룹 선택 없이 종전 1문서 흐름.
        if (outcome === 'opened') {
          setComposedEntryTemplateKey(null);
          return;
        }
      } catch (err) {
        // 여기서 종전 흐름으로 흘려보내면 사용자가 고를 기회 없이 **1건이 만들어진다** —
        // 무엇이 생기는지가 달라지므로 조용히 넘기지 않고 재시도를 제안한다.
        // ⛔ `setIsPreparingComposed(false)`를 하지 «않는다» — 재시도 버튼은 **준비 화면의 실패 갈래**
        //    (`isPreparingComposed && error`)에만 있다. 종전엔 여기서 false로 내려 기본 화면으로 돌아가
        //    오류 문구만 보이고 `retryFn`을 세팅해도 **재시도 수단에 도달할 수 없었다**(리뷰 1103 F19).
        //    실패 화면의 「취소」(`cancelPreparing`)가 준비 상태를 내린다.
        // ⚠️ `extractApiError(...).message`를 쓰지 말 것 — 그 헬퍼는 `NETWORK_ERROR`·`UNKNOWN_ERROR`에도
        // 문구를 **늘 채우므로**(`lib/api.ts:378`) `||` 폴백이 영영 발동하지 않고, 네트워크 단절에
        // 「Request failed with status code 500」이 그대로 뜬다. 정본은 `apiErrorMessage`(`:409`)이고
        // 그 둘은 null을 돌려 아래 일반 문구로 떨어진다 — 그 JSDoc(`:395`)이 이 사고를 적고 있다.
        setError(apiErrorMessage(err) ?? '생성 범위를 확인하지 못했습니다.');
        setRetryFn(() => () => handleSelectComposedTemplate(tpl, entryGuidance));
        return;
      }
    }

    await prepareAndEnterComposed(tpl, entryGuidance);
  };

  /**
   * COMPOSED 작성 페이지 진입(1문서 경로) — AI 후보 사전 생성 후 이동.
   *
   * 분할 모달의 **통합(combined)** 확정도 여기로 수렴한다. 통합은 "분할하지 않음 = 1건"이라
   * 종전 경로 그대로여야 하고(결정 §③-1), 갈라 두면 AI 사전 생성이 한쪽에서만 돈다.
   */
  const prepareAndEnterComposed = async (
    tpl: ReportTemplate,
    entryGuidance?: string,
    // 점검 선택 단계(`policy.allowMultipleInspections`)가 확정한 점검 id — **정렬된 순서**
    // (InspectionSelectModal이 createdAt×정렬 옵션으로 정렬해 넘긴다). 그 순서가 생성 POST의
    // inspectionIds → junction sortOrder가 되어 서버 합본 조립의 점검 순서가 된다. 미지정이면
    // 현재 점검 하나(종전 경로).
    selectedInspectionIds?: string[],
  ) => {
    const navigateToComposed = () => {
      onClose();
      // 현재 점검 하나만 골랐으면 종전 단일 진입과 같은 URL(경로 동일성 — new-composed는
      // 둘을 같은 배열로 읽지만, 단일 흐름의 URL·spec을 그대로 둔다).
      const ids = selectedInspectionIds ?? [];
      const isCurrentOnly = ids.length === 0 || (ids.length === 1 && ids[0] === inspectionId);
      const inspectionQuery = isCurrentOnly
        ? `inspectionId=${inspectionId}`
        : `inspectionIds=${ids.map((id) => encodeURIComponent(id)).join(',')}`;
      navigate(
        `/reports/new/composed/${encodeURIComponent(tpl.key)}?${inspectionQuery}&siteId=${siteId}`,
        { state: { from: location.pathname, fromLabel: '점검 상세' } },
      );
    };

    // AI 후보 파트(sortableSelect 소스) — 진입 전 사전 생성을 로딩으로 대기(2026-07-16 사용자 확정:
    // 사용자 입력이 있을 때뿐 아니라 캐시가 비어 생성이 필요한 경우도 동일). 캐시가 있으면 서버가
    // 즉시 반환(생성 0 — 로딩 순간 통과), 입력이 이전과 같으면 동일성 트리거가 재사용. 진입 후
    // new-composed의 재요청은 캐시 히트라 이중 생성 없음(구 SMS 사전 생성 동형).
    const improvementParts = collectImprovementParts(tpl.composerWidgetTree);
    const needImprovement = improvementParts.length > 0 && !!companyId && !!inspectionId;
    if (!needImprovement) {
      navigateToComposed();
      return;
    }

    setIsPreparingComposed(true);
    setPreparingStep('ai');
    setError(null);
    setRetryFn(null);

    // 사전 생성 — allSettled로 실패해도 진입 진행(작성 페이지가 후보 없음/재시도 처리)
    await Promise.allSettled([
      generateImprovementCandidates(inspectionId, companyId, entryGuidance, improvementParts),
    ]);
    navigateToComposed();
  };

  /**
   * Composed 진입 전 입력 패널 토글 — 열 때 무지시 사전 조회(캐시 우선)로 저장된 입력 원문을 기본값에 채운다.
   * 사용자가 이미 타이핑한 값은 덮어쓰지 않는다(빈 값일 때만 채움).
   */
  const toggleComposedEntryPanel = (tpl: ReportTemplate, entryInput: EntryUserInputMeta) => {
    if (composedEntryTemplateKey === tpl.key) {
      setComposedEntryTemplateKey(null);
      return;
    }
    setComposedEntryValue('');
    setComposedEntryTemplateKey(tpl.key);
    if (companyId && inspectionId) {
      generateImprovementCandidates(inspectionId, companyId, undefined, entryInput.parts)
        .then((res) => {
          if (res.guidanceInput) {
            const stored = res.guidanceInput;
            setComposedEntryValue((cur) => (cur ? cur : stored));
          }
        })
        .catch(() => { /* 사전 조회 실패 무시 — 빈 입력으로 진행 */ });
    }
    setTimeout(
      () => composedEntryPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      100,
    );
  };

  /**
   * "작성 시작" — 입력값(trim, 빈 값 제거)을 템플릿 키별 sessionStorage에 저장 후 진입 플로우 실행.
   * 입력값은 사전 생성 요청에도 동봉(handleSelectComposedTemplate가 로딩으로 완료 대기) — 진입 후
   * new-composed의 동일 입력 재요청은 동일성 트리거 캐시 히트(이중 생성 없음).
   */
  const handleStartComposedWithGuidance = (tpl: ReportTemplate) => {
    const trimmed = composedEntryValue.trim();
    const key = composedEntryGuidanceKey(tpl.key);
    if (trimmed) sessionStorage.setItem(key, trimmed);
    else sessionStorage.removeItem(key);
    setComposedEntryTemplateKey(null);
    handleSelectComposedTemplate(tpl, trimmed || undefined);
  };

  /** COMPOSED 위임 모달에서 "본인 작성" 선택 → 기존 직접 작성 흐름(AI 사전 생성 후 진입).
   *  진입 전 사용자 입력(entryUserInput)이 켜져 있으면 직접 작성도 입력 패널을 경유한다
   *  (2026-07-25 — 종전엔 위임 가능 템플릿에서 패널이 통째로 우회됐음). 위임 선택 경로는
   *  담당자 흐름이라 입력 미수집 유지. */
  const handleComposedSelfSelected = (tpl: ReportTemplate) => {
    setComposedDelegationTemplate(null);
    const entryInput = tpl.entryUserInput
      ? findEntryUserInputMeta(tpl.composerWidgetTree)
      : null;
    if (entryInput) {
      toggleComposedEntryPanel(tpl, entryInput);
      return;
    }
    handleSelectComposedTemplate(tpl);
  };

  /**
   * COMPOSED 위임 모달에서 "담당자에게 위임" 확인 (DLR 위임 동형).
   *
   * ⚠️ **실패를 여기서 표시하지 않는다**(2026-09-10) — 그냥 던져 자식(위임 모달)이 잡게 둔다.
   * 사용자가 보고 있는 자리는 위임 모달(sub 층)이고, 이 화면은 그 **뒤에 깔려 있다**. 둘 다
   * 표시하면 한 에러가 두 곳에 갈라지고 뒤 화면에 유령 메시지가 남는다(사용자 실측: 위임 게이트
   * 400 하나가 여기엔 서버 메시지로, 정작 보이는 모달엔 axios raw로 떴다 — 표시 자리가 갈린 채
   * 하필 보이는 쪽이 raw였다).
   *
   * ⚠️ 던지는 값은 **원본(axios 에러) 그대로**여야 한다 — `new Error(message)`로 바꿔 던지면
   * 자식이 응답 본문을 잃어 `apiErrorMessage`가 null을 주고, 서버 안내 대신 일반 문구로 떨어진다.
   */
  const handleComposedDelegateSelected = async (
    tpl: ReportTemplate,
    { contact, notifyChannels, approvalLines, entryGuidance }: { contact: SiteContact; notifyChannels: ('sms' | 'email')[]; approvalLines?: DelegationApprovalLineValue; entryGuidance?: string },
  ) => {
    setError(null);
    const result = await createDelegatedComposedReport({
      companyId,
      siteId,
      templateKey: tpl.key,
      title: tpl.name,
      ...(inspectionId ? { inspectionIds: [inspectionId] } : {}),
      delegatedContactId: contact.id,
      notifyChannels,
      // 진입 전 사용자 입력(위임 모달 지시문) — 서버 guidance 사전 생성에 동봉(직접 흐름
      // generate-improvement-candidates의 userGuidance와 동일 파라미터명).
      ...(entryGuidance ? { userGuidance: entryGuidance } : {}),
      ...(approvalLines && approvalLines.additionalLines.length > 0
        ? { delegationApprovalLines: approvalLines.additionalLines }
        : {}),
    });
    const token = result.draftToken;
    const sent: string[] = [];
    const failed: string[] = [];
    if (token?.smsStatus === 'SENT') sent.push('SMS');
    else if (token?.smsStatus === 'FAILED') failed.push('SMS');
    if (token?.emailStatus === 'SENT') sent.push('이메일');
    else if (token?.emailStatus === 'FAILED') failed.push('이메일');
    let msg = `${contact.name}님에게 ${tpl.name} 작성을 위임했습니다.`;
    if (sent.length > 0) msg += ` (${sent.join(', ')} 발송 완료)`;
    if (failed.length > 0) msg += ` ${failed.join(', ')} 발송 실패`;
    showToast(msg, failed.length > 0 ? 'warning' : 'success');
    setComposedDelegationTemplate(null);
    onClose();
    // 방금 만든 문서로 보낸다. 목적지는 손으로 정하지 않고 **흐름 레지스트리**가 정한다 —
    // 「CREATING + 위임됨」의 정위치는 이미 상세로 선언돼 있고(`resolvePostSubmitEntryRoute`
    // → `{kind:'delegation'}`), 목록 클릭·직접 URL은 상세의 진입 라우팅으로 그 규약을 탄다.
    // **생성 직후만 목록으로 던져** 일관성이 깨져 있었다(2026-09-10 사용자 적발): 방금 만든
    // 문서를 눈으로 찾아야 했고, 직후에 볼 것(위임 진행 표시·재전송/교체/해제 진입점)은
    // 전부 상세에 있다. 분할로 확장돼도 **그룹 1 = placeholder in-place**라 이 id는 안 죽는다.
    // 레지스트리를 거치므로 나중에 위임 현황 뷰(W4b)가 href를 바꾸면 여기도 따라간다.
    navigate(resolvePostSubmitEntryRoute(result.report, currentUserId).href);
  };

  // 로딩 중 UI (Composer 준비 — 점검 정보 로드 → AI 제안 사전 생성)
  if (isPreparingComposed) {
    // 액센트는 **완성된 클래스 리터럴**로 고른다 — `bg-${color}-600`처럼 조립하면 Tailwind가
    // 스캔에서 못 찾아 CSS를 생성하지 않고, 클래스는 붙었는데 무스타일이 되는 조용한 실패가 된다.
    const accentBg100 = 'bg-orange-100';
    const accentBg600 = 'bg-orange-600';
    const accentText600 = 'text-orange-600';
    const accentBorder600 = 'border-orange-600';
    const hasError = !!error;
    const cancelPreparing = () => {
      setError(null);
      setRetryFn(null);
      setIsPreparingComposed(false);
    };
    // 준비 중에는 닫을 수단이 없다(종전과 같다 — 오버레이 클릭도 무반응이었다). 실패 화면에서만
    // Esc가 「취소」 버튼과 같은 일을 한다(base 규약 — 그 화면엔 원래 취소가 있다).
    return (
      <Modal
        open
        onClose={cancelPreparing}
        ariaLabel={
          hasError
            ? '데이터 준비 실패'
            : preparingStep === 'loading'
              ? '점검 정보를 불러오는 중...'
              : 'AI 제안 내용을 준비하고 있습니다'
        }
        size="md"
        overlayZClassName={MODAL_LAYER.root}
        closeOnOverlay={false}
        closeOnEsc={hasError}
        showCloseButton={false}
        closeLocked={!hasError}
      >
        {/* 여백은 종전 `px-8 py-10`과 같다 — base 본문(`px-5 py-4`) + 여기(`px-3 py-6`). */}
        <div className="px-3 py-6">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 ${hasError ? 'bg-red-100' : accentBg100} rounded-full flex items-center justify-center mb-4`}>
              {hasError ? (
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-orange-600 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
            </div>
            {hasError ? (
              <>
                <h3 className="text-lg font-medium text-ink-900 mb-2">데이터 준비 실패</h3>
                <p className="text-sm text-red-600 text-center mb-6">{error}</p>
                <div className="flex items-center gap-3">
                  {retryFn && (
                    <Button variant="primary" onClick={() => { setError(null); retryFn(); }}>
                      재시도
                    </Button>
                  )}
                  <Button variant="secondary" onClick={cancelPreparing}>
                    취소
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-ink-900 mb-2">
                  {preparingStep === 'loading' ? '점검 정보를 불러오는 중...' : 'AI 제안 내용을 준비하고 있습니다'}
                </h3>
                <p className="text-sm text-ink-500 text-center mb-4">
                  점검 결과를 분석하여 작성에 필요한 AI 제안 내용을 미리 생성합니다.
                </p>
                <div className={`flex items-center gap-2 ${accentText600}`}>
                  <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${accentBorder600}`}></div>
                  <span className="text-sm">완료까지 잠시 시간이 걸립니다</span>
                </div>
                {/* 진행 단계 표시 (Composer 양식 진입 전 AI 제안 사전 생성) */}
                <div className="mt-6 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${preparingStep === 'loading' ? accentBg600 : 'bg-green-500'}`} />
                  <span className={`text-xs ${preparingStep === 'loading' ? `${accentText600} font-medium` : 'text-green-600'}`}>
                    데이터 로드
                  </span>
                  <div className="w-4 h-px bg-ink-300" />
                  <div className={`w-2 h-2 rounded-full ${preparingStep === 'ai' ? accentBg600 : 'bg-ink-300'}`} />
                  <span className={`text-xs ${preparingStep === 'ai' ? `${accentText600} font-medium` : 'text-ink-400'}`}>
                    AI 제안
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <>
    {/* 오버레이·Esc·✕ 닫기는 전부 `handleClose`(입력 패널 초기화 + onClose)로 수렴한다.
        본문 스크롤은 base `bodyScroll`(기본)이 소유하고 헤더·푸터는 고정이다. */}
    <Modal
      open
      onClose={handleClose}
      title="문서 유형 선택"
      size="md"
      overlayZClassName={MODAL_LAYER.root}
      footer={
        <Button variant="secondary" onClick={handleClose} className="w-full">
          취소
        </Button>
      }
    >
      <p className="mb-4 text-sm text-ink-500">
        생성할 문서 유형을 선택해주세요.
      </p>
      <div>
        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 rounded-card bg-red-50 p-3">
            <div className="flex items-start">
              <ExclamationIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-2 flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-400 hover:text-red-500"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Composer enabled 템플릿 카드 — 서버가 category·entitlement로 필터한 목록 그대로.
            손나열 카드와 그 노출 판정을 두지 않는다. */}
        <div className="space-y-3">
          {composerTemplates.map((tpl) => {
            // delegationMeta.enabled 템플릿은 직접/위임 선택 모달 오픈, 그 외는 기존 직접 작성 진입
            const canDelegate = !!tpl.delegationMeta?.enabled;
            // 진입 전 사용자 입력 — 템플릿 전역 entryUserInput 게이트. 켜졌을 때 트리에서
            // userInput 메타 파트를 찾아 입력 패널 문구를 특정. 위임 가능 템플릿도 파생한다
            // (카드 클릭은 위임 선택 모달 우선이지만, "본인 작성" 선택 시 패널이 여기 렌더
            // 게이트를 통과해야 함 — 2026-07-25). 위임 선택 경로만 입력 미수집.
            const entryInput = tpl.entryUserInput
              ? findEntryUserInputMeta(tpl.composerWidgetTree)
              : null;
            const showEntryPanel = composedEntryTemplateKey === tpl.key;
            // 이 점검으로 같은 템플릿의 COMPOSED 보고서가 이미 있으면 중복 생성 차단
            // (여러 건 허용 템플릿은 여기서 잠그지 않는다 — allowsMultiple)
            const alreadyCreated = isComposedAlreadyCreated(tpl);
            return (
            <div
              key={tpl.key}
              className="relative"
              // 잠긴 카드의 안내는 네이티브 title로 — 커스텀 툴팁은 목록 스크롤 영역(overflow) 밖으로
              // 나가는 마지막 카드에서 잘린다. disabled 버튼은 마우스 이벤트가 없으니 래퍼에 단다.
              title={alreadyCreated ? '이 점검으로 이미 생성되었습니다' : undefined}
            >
            <button
              type="button"
              onClick={() => {
                if (alreadyCreated) return;
                if (canDelegate) setComposedDelegationTemplate(tpl);
                else if (entryInput) toggleComposedEntryPanel(tpl, entryInput);
                else handleSelectComposedTemplate(tpl);
              }}
              disabled={isPreparingComposed || alreadyCreated}
              className={`w-full flex items-center gap-4 p-4 border-2 rounded-card transition-colors group disabled:opacity-60 disabled:cursor-not-allowed ${
                alreadyCreated
                  ? 'border-line bg-surface-soft'
                  : showEntryPanel
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-line hover:border-teal-500 hover:bg-teal-50'
              }`}
            >
              <div className={`flex-shrink-0 w-12 h-12 rounded-card flex items-center justify-center ${
                alreadyCreated ? 'bg-surface-hover' : 'bg-teal-100 group-hover:bg-teal-200'
              }`}>
                <ComposerIcon className={`w-6 h-6 ${alreadyCreated ? 'text-ink-400' : 'text-teal-600'}`} />
              </div>
              <div className="flex-1 text-left min-w-0">
                {/* 설명 줄은 두지 않는다 — 모든 카드가 Composer 양식이라 문구가 사실상 같아진다.
                    위임 가능 여부는 카드를 누른 뒤 직접/위임 선택 모달이 말한다. */}
                <div className={`font-medium truncate ${alreadyCreated ? 'text-ink-500' : 'text-ink-900'}`}>
                  {tpl.name}
                  {alreadyCreated && (
                    <Badge tone="slate" shape="rounded" className="ml-2">이미 생성됨</Badge>
                  )}
                </div>
              </div>
              <ChevronRightIcon className={`w-5 h-5 transition-transform ${
                alreadyCreated
                  ? 'text-ink-300'
                  : showEntryPanel
                    ? 'rotate-90 text-teal-500'
                    : 'text-ink-400 group-hover:text-teal-500'
              }`} />
            </button>

            {/* 진입 전 사용자 입력 패널 (템플릿 전역 entryUserInput 켜짐 시) */}
            {showEntryPanel && entryInput && !alreadyCreated && (
              <div ref={composedEntryPanelRef} className="border border-teal-200 rounded-card p-3 bg-teal-50/50 mt-2 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    {entryInput.label} <span className="text-xs text-ink-400">(선택)</span>
                  </label>
                  <textarea
                    value={composedEntryValue}
                    onChange={(e) => setComposedEntryValue(e.target.value)}
                    rows={4}
                    disabled={isPreparingComposed}
                    className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50"
                    placeholder={entryInput.placeholder}
                  />
                  <p className="mt-1 text-xs text-ink-400">
                    입력한 내용이 첫 AI 생성에 반영됩니다. 같은 입력이면 다시 생성하지 않습니다.
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isPreparingComposed}
                    onClick={() => handleStartComposedWithGuidance(tpl)}
                  >
                    작성 시작
                  </Button>
                </div>
              </div>
            )}
            </div>
            );
          })}
        </div>
      </div>
    </Modal>

    <InspectionSelectModal
      // 층은 여는 쪽이 정한다 — 이 모달(root) 위.
      layer={MODAL_LAYER.sub}
      isOpen={composedInspectionSelect !== null}
      onClose={() => setComposedInspectionSelect(null)}
      onConfirm={async (inspectionIds) => {
        // Composer 템플릿의 점검 선택(`policy.allowMultipleInspections`) — 고른 점검(정렬된
        // 순서)으로 1문서 진입.
        if (!composedInspectionSelect) return;
        const { tpl, entryGuidance } = composedInspectionSelect;
        setComposedInspectionSelect(null);
        await prepareAndEnterComposed(tpl, entryGuidance, inspectionIds);
      }}
      companyId={companyId}
      siteId={siteId}
      defaultInspectionId={inspectionId}
    />
    {composedDelegationTemplate && (
      <ReportDelegationModal
        isOpen={true}
        // 층은 여는 쪽이 정한다 — 이 모달(root) 위.
        layer={MODAL_LAYER.sub}
        onClose={() => setComposedDelegationTemplate(null)}
        companyId={companyId}
        siteId={siteId}
        reportTypeLabel={composedDelegationTemplate.name}
        template={composedDelegationTemplate}
        onSelfSelected={() => handleComposedSelfSelected(composedDelegationTemplate)}
        onDelegateSelected={(params) => handleComposedDelegateSelected(composedDelegationTemplate, params)}
      />
    )}
    {splitEntry.modal}
    </>
  );
}

// Icons
function ComposerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

export default ReportTypeSelectModal;
